// Explicit native-runtime → web RealmVibe key handoff.
//
// Android/iOS obtains the 64-byte local RealmVibe file key from
// flutter_secure_storage and the authenticated register/login handover
// delivers it to this page in a one-time bundle. This module never generates
// a browser key and never persists the raw key in localStorage or IndexedDB.

const DATABASE_KEY_LENGTH = 64;

let installedKey = null;
let waiters = [];
let generation = {};

function cancelled() {
  const error = new Error('Native database-key handoff was cleared or replaced');
  error.name = 'AbortError';
  return error;
}

function deliver({ key, token }) {
  // A promise can already be resolved while its consumer is still queued.
  // Refuse stale delivery after clear/replacement, not only pending waiters.
  if (token !== generation) {
    key.fill(0);
    throw cancelled();
  }
  return key;
}

function normalizeKey(value) {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  if (bytes.byteLength !== DATABASE_KEY_LENGTH) {
    throw new Error(
      `[NativeDatabaseKeyHandoff] invalid key length: ${bytes.byteLength}`
    );
  }
  return new Uint8Array(bytes);
}

export function installDatabaseKey(value) {
  const key = normalizeKey(value);
  if (installedKey != null) installedKey.fill(0);
  generation = {};
  installedKey = key;
  const pending = waiters;
  waiters = [];
  for (const { resolve } of pending) {
    resolve({ key: new Uint8Array(key), token: generation });
  }
  return Promise.resolve();
}

export function waitForDatabaseKey() {
  if (installedKey != null) {
    return Promise.resolve({ key: new Uint8Array(installedKey), token: generation }).then(deliver);
  }
  return new Promise((resolve, reject) => {
    waiters.push({ resolve, reject });
  }).then(deliver);
}

export function clearDatabaseKey() {
  generation = {};
  if (installedKey != null) {
    installedKey.fill(0);
    installedKey = null;
  }
  const pending = waiters;
  waiters = [];
  for (const { reject } of pending) reject(cancelled());
}
